import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "wouter";
import dayjs from "dayjs";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Filter,
  MapPin,
  MessageSquare,
  Plus,
  Users,
  X,
} from "lucide-react";
import { Loader } from "../components/Loader";
import {
  useEventsList,
  useAllEvents,
  type EventCard as EventCardType,
  type EventCarousel,
  type OpenKiki,
} from "../lib/api/useEventsList";
import { useProfile } from "../lib/api/useProfile";
import { useSearchCities } from "../lib/api/useCities";
import { getEventType } from "@shared/eventTypes";
import { isSapphicUser } from "@shared/quizAnswers";
import {
  parseUTCDate,
  formatRelativeEventDate,
  formatTimeToDisplay,
} from "../utils/dateUtils";
import { EventPoster } from "../components/EventPoster";
import { EventTypeSelectModal } from "../components/EventTypeSelectModal";
import {
  CitySelectModal,
  type SelectedCity,
} from "../components/CitySelectModal";
import {
  DateFilterModal,
  formatDateRange,
} from "../components/DateFilterModal";
import { useCustomEvents } from "../lib/features";
import { useIsSignedIn } from "../lib/platform/AuthProvider";
import { useAuth } from "../lib/platform";
import { EventCard as EventCardComponent } from "../components/EventCard";
import { CategoryLanding } from "../components/CategoryLanding";
import { CarouselRail } from "../components/EventCarouselRail";
import { KikiCarousel } from "../components/KikiCarousel";
import {
  OnboardingProvider,
  useOnboardingGate,
} from "../components/OnboardingContext";
import {
  EVENT_CATEGORIES,
  getCategoryDates,
  type EventCategory,
} from "../lib/eventCategories";
import { useDragScroll } from "../lib/useDragScroll";
import tracking from "../lib/tracking";
import { useT } from "@/lib/i18n";
import { SiteHeader } from "../components/SiteHeader";
import { LandingFooter } from "../components/LandingFooter";
import FeedbackDialog from "../components/FeedbackDialog";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const STORAGE = {
  types: "outclose_events_types",
  city: "outclose_events_city",
  excluded: "outclose_events_excluded",
  mood: "outclose_events_mood",
  betaBannerDismissed: "outclose_events_beta_banner_dismissed",
} as const;

function readStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: unknown): void {
  try {
    if (value === null || (Array.isArray(value) && value.length === 0)) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch (_) {
    /* ignore */
  }
}

function readFiltersFromUrl(): {
  types: string[];
  cityId: string | null;
  dateFrom: string | null;
  dateTo: string | null;
} {
  if (typeof window === "undefined")
    return { types: [], cityId: null, dateFrom: null, dateTo: null };
  const params = new URLSearchParams(window.location.search);
  const raw = (key: string) => {
    const v = params.get(key) || null;
    return v && ISO_DATE.test(v) ? v : null;
  };
  return {
    types: (params.get("t") ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    cityId: params.get("city") || null,
    dateFrom: raw("dateFrom"),
    dateTo: raw("dateTo"),
  };
}

// Curated per-city hero photos for the knockout header. Placeholder URLs for now
// — swap in licensed assets hosted on our own CDN. Cities with no entry fall back
// to the brand-gradient hero.
const CITY_HERO_IMAGES: Record<string, string> = {
  usa_nyc: "/img/hero/nyc.png",
  usa_la: "/img/hero/los-angeles.png",
  usa_chicago: "/img/hero/chicago.jpg",
  usa_atlanta: "/img/hero/atlanta.jpg",
  usa_miami: "/img/hero/Miami.png",
  usa_sf: "/img/hero/san-francisco.png",
  usa_dc: "/img/hero/DC.png",
  uk_london: "/img/hero/London.png",
  ca_toronto: "/img/hero/Toronto.png",
  usa_boston: "/img/hero/Boston.png",
  usa_dallas: "/img/hero/Dallas.png",
  usa_fort_lauderdale: "/img/hero/fort-lauderdale.png",
  usa_philadelphia: "/img/hero/philadelphia.png",
  usa_san_diego: "/img/hero/san-diego.png",
  de_berlin: "/img/hero/Berlin.png",
  fr_paris: "/img/hero/paris.png",
  au_sydney: "/img/hero/Sydney.png",
  au_melbourne: "/img/hero/Melbourne.png",
};

// Per-city hero wordmark font — all self-hosted display faces from FontShare,
// Velvetyne, and a couple of OFL one-offs (see client/src/styles/fonts.css), no
// Google. Chosen for character over the hero photo; the readability scrim below
// the image lets us run delicate/high-contrast faces. NYC → Boska (editorial
// high-contrast), LA → Telma (warm retro display), Chicago → Array
// (bold blocky retro display — characterful but very legible), Atlanta →
// Anthony (heavy soft blobby display),
// Miami → Melodrama (sultry glam), San Francisco → Gambetta (calligraphic),
// Washington DC → Erode (organic inscriptional), London → Zodiak (timeless
// didone), Toronto → VG5000 (pixel/retro-digital), Boston → Basteleur (quirky
// bricolage serif), Dallas → Tanker (heavy condensed), Fort Lauderdale → Quilon
// (relaxed calligraphic serif), Philadelphia → Bespoke Slab (historic slab),
// San Diego → Gulax (funky angular geometric), Berlin → UnifrakturCook (German
// Fraktur), Paris → Le Murmure (idiosyncratic French display), Sydney → Excon
// (wide expanded grotesque), Melbourne → Pilowlava (liquid experimental).
// Any city without an entry falls back to Sentient (see the wordmark below).
const CITY_HERO_FONT: Record<string, string> = {
  usa_nyc: "'Boska', serif",
  usa_la: "'Telma', serif",
  usa_chicago: "'Array', sans-serif",
  usa_atlanta: "'Anthony', sans-serif",
  usa_miami: "'Melodrama', serif",
  usa_sf: "'Gambetta', serif",
  usa_dc: "'Erode', serif",
  uk_london: "'Zodiak', serif",
  ca_toronto: "'VG5000', sans-serif",
  usa_boston: "'Basteleur', serif",
  usa_dallas: "'Tanker', sans-serif",
  usa_fort_lauderdale: "'Quilon', serif",
  usa_philadelphia: "'Bespoke Slab', serif",
  usa_san_diego: "'Gulax', sans-serif",
  de_berlin: "'UnifrakturCook', cursive",
  fr_paris: "'Le Murmure', sans-serif",
  au_sydney: "'Excon', sans-serif",
  au_melbourne: "'Pilowlava', sans-serif",
};

// Per-city framing for the hero image. An entry overrides object-position /
// scale / origin (and the bottom-fade mask) for that city; every other city
// falls back to HERO_IMG_DEFAULT_CLASS and keeps the original framing.
const HERO_IMG_DEFAULT_CLASS =
  "object-bottom origin-bottom scale-[1.2] sm:scale-100 sm:origin-center sm:object-[center_94%] [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_70%,transparent)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_70%,transparent)]";

const CITY_HERO_IMG_CLASS: Record<string, string> = {
  usa_la:
    "object-[center_30%] origin-center scale-[1.3] sm:scale-110 sm:origin-center sm:object-[center_30%] [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_60%,transparent_85%)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_60%,transparent_85%)]",
  usa_chicago:
    "object-[center_42%] origin-center scale-[1.15] sm:scale-105 sm:object-[center_40%] blur-[2px] [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_62%,transparent_92%)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_62%,transparent_92%)]",
  usa_atlanta:
    "object-[center_40%] origin-center scale-[1.1] sm:scale-105 sm:object-[center_38%] [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_64%,transparent_94%)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_64%,transparent_94%)]",
  uk_london:
    "object-[center_30%] origin-center scale-[1.2] sm:scale-100 sm:object-[center_30%] [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_70%,transparent)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_70%,transparent)]",
  usa_boston:
    "object-[center_35%] origin-center scale-[1.2] sm:scale-100 sm:object-[center_35%] [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_70%,transparent)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_70%,transparent)]",
  usa_nyc:
    "object-[center_90%] origin-center scale-[1.2] sm:scale-100 sm:object-[center_90%] [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_70%,transparent)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0.9)_70%,transparent)]",
};

// Per-city film-grain overlay tuning. Each entry adds a grayscale noise layer
// (mix-blend-overlay) over the hero photo: `opacity` controls intensity and
// `size` how coarse the grain tiles are (smaller = finer, denser). Miami runs
// the heaviest — full opacity, fine dense grain — for a gritty Vice texture.
const CITY_HERO_GRAIN: Record<string, { opacity: string; size: string }> = {
  usa_la: { opacity: "opacity-60", size: "200px 200px" },
  usa_chicago: { opacity: "opacity-80", size: "150px 150px" },
  usa_miami: { opacity: "opacity-100", size: "110px 110px" },
};

// Timeframe presets for the inline filter composer (Posh-style). Each maps to a
// concrete date range; "Anytime" clears the filter.
const TIMEFRAMES: { key: string; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "this-weekend", label: "This weekend" },
  { key: "this-week", label: "This week" },
  { key: "this-month", label: "This month" },
  { key: "anytime", label: "Anytime" },
];

function timeframeRange(key: string): { from: string | null; to: string | null } {
  switch (key) {
    case "today":
      return getCategoryDates("today");
    case "this-weekend":
      return getCategoryDates("weekend");
    case "this-week":
      return {
        from: dayjs().format("YYYY-MM-DD"),
        to: dayjs().endOf("week").format("YYYY-MM-DD"),
      };
    case "this-month":
      return {
        from: dayjs().format("YYYY-MM-DD"),
        to: dayjs().endOf("month").format("YYYY-MM-DD"),
      };
    default:
      return { from: null, to: null };
  }
}

// Context-aware default: on Fri/Sat show "this weekend", otherwise "this week".
function smartTimeframeKey(): string {
  const day = dayjs().day(); // 0 Sun … 6 Sat
  return day === 5 || day === 6 ? "this-weekend" : "this-week";
}

// The curated tags exposed in the strip, in order. Everything else opens via the
// "More" pill (the full tag picker).
const FEATURED_TAG_IDS = ["gay", "sapphic", "arts", "wellness", "nightlife"];

// Gay and sapphic are mutually-exclusive identity tags in the strip — at most one
// may be selected, alongside at most one other ("activity") tag.
const IDENTITY_CATEGORIES = ["gay", "sapphic"];

export default function EventsIndex() {
  const { t } = useT("events");
  const isSignedIn = useIsSignedIn();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [betaBannerDismissed, setBetaBannerDismissed] = useState(
    () => readStorage<boolean>(STORAGE.betaBannerDismissed) === true,
  );

  const dismissBetaBanner = () => {
    setBetaBannerDismissed(true);
    writeStorage(STORAGE.betaBannerDismissed, true);
  };
  const { ref: categoryStripRef, dragHandlers: categoryStripDrag } =
    useDragScroll<HTMLDivElement>();
  const initial = readFiltersFromUrl();
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    initial.types.length > 0
      ? initial.types
      : (readStorage<string[]>(STORAGE.types) ?? []),
  );
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState<string | null>(initial.dateFrom);
  const [dateTo, setDateTo] = useState<string | null>(initial.dateTo);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  // City is stored as a SelectedCity so we can render the chip without
  // a second fetch; only the id is mirrored to the URL.
  const [selectedCity, setSelectedCity] = useState<SelectedCity | null>(
    initial.cityId
      ? { id: initial.cityId, name: initial.cityId, emoji: "📍" }
      : readStorage<SelectedCity>(STORAGE.city),
  );
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [activeMood] = useState<string | null>(
    readStorage<string>(STORAGE.mood),
  );

  const customEventsEnabled = useCustomEvents();

  const { data: profileData } = useProfile();
  // listCities RPC: empty query + activeOnly returns the geo-closest active
  // city first (server uses the request's lat/lon), so data[0] is the fallback.
  const { data: closestData } = useSearchCities("", undefined, 1, true);
  // Active cities for the inline city dropdown.
  const { data: citiesData } = useSearchCities("", undefined, 50, true);

  const profileUser = profileData?.success
    ? (profileData.data?.user as
        | { city?: string | null; cityName?: string | null }
        | undefined)
    : undefined;
  const closestCity = closestData?.success ? closestData.data?.[0] : undefined;
  // Outclose dinners are gendered tables — label the card for the viewer.
  const guestNoun = guestNounFor(
    profileData?.success ? (profileData.data?.profile as any) : null,
  );

  // A deep-linked / stored city only knows its id ("usa_nyc") until a query
  // resolves the real name — so resolve it from the active-cities list (cached,
  // usually instant) to avoid showing the raw id.
  const cityList = citiesData?.success ? (citiesData.data ?? []) : [];
  const resolveCityName = (id: string, fallback: string) =>
    cityList.find((c) => c.id === id)?.name ?? fallback;
  // When the user hasn't explicitly picked a city, fall back to their profile
  // city, then the geo-closest active city.
  const effectiveCity = selectedCity
    ? {
        id: selectedCity.id,
        name: resolveCityName(selectedCity.id, selectedCity.name),
      }
    : profileUser?.city
      ? {
          id: profileUser.city,
          name:
            profileUser.cityName ??
            resolveCityName(profileUser.city, profileUser.city),
        }
      : closestCity
        ? { id: closestCity.id, name: closestCity.name }
        : null;

  // Inline composer: derive the active timeframe label/key from the date range so
  // it stays in sync with the category chips and the date modal.
  const currentTimeframeKey = useMemo(() => {
    if (!dateFrom && !dateTo) return "anytime";
    for (const tf of TIMEFRAMES) {
      if (tf.key === "anytime") continue;
      const r = timeframeRange(tf.key);
      if (r.from === dateFrom && r.to === dateTo) return tf.key;
    }
    return "custom";
  }, [dateFrom, dateTo]);
  const timeframeLabel =
    currentTimeframeKey === "custom"
      ? formatDateRange(dateFrom, dateTo)
      : (TIMEFRAMES.find((t) => t.key === currentTimeframeKey)?.label ??
        "Anytime");
  const cityOptions = citiesData?.success ? (citiesData.data ?? []) : [];

  const { data, isLoading, error } = useEventsList({
    types: selectedTypes,
    city: effectiveCity?.id ?? null,
    dateFrom,
    dateTo,
    mood: activeMood,
  });

  // Tag catalog (type-unfiltered, but matching the current city + timeframe) —
  // drives which tags are shown. Type-unfiltered so picking a tag doesn't collapse
  // the set; timeframe-matched so any shown tag actually has events in view.
  const { data: catalogData } = useEventsList({
    types: [],
    city: effectiveCity?.id ?? null,
    dateFrom,
    dateTo,
    mood: null,
  });

  useEffect(() => {
    document.title = t("meta.title");
    tracking.eventsIndexViewed(isSignedIn);
    // Fire once on mount; sign-in state at first paint is the relevant cut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default the timeframe to a context-aware "this week"/"this weekend" on first
  // load (Posh-style), unless the URL already carried an explicit date range.
  useEffect(() => {
    if (!initial.dateFrom && !initial.dateTo) {
      const r = timeframeRange(smartTimeframeKey());
      setDateFrom(r.from);
      setDateTo(r.to);
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror filter state into the URL so the view is shareable/bookmarkable.
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTypes.length > 0) params.set("t", selectedTypes.join(","));
    if (selectedCity?.id) params.set("city", selectedCity.id);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    const search = params.toString();
    const target = window.location.pathname + (search ? `?${search}` : "");
    if (target !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, "", target);
    }
  }, [selectedTypes, selectedCity, dateFrom, dateTo]);

  const [excludedTypes] = useState<string[]>(
    readStorage<string[]>(STORAGE.excluded) ?? [],
  );

  // Persist filter preferences to localStorage so they survive navigation and reopens.
  // Dates are intentionally excluded — they go stale.
  useEffect(() => {
    writeStorage(STORAGE.types, selectedTypes);
  }, [selectedTypes]);
  useEffect(() => {
    writeStorage(STORAGE.city, selectedCity);
  }, [selectedCity]);
  useEffect(() => {
    writeStorage(STORAGE.excluded, excludedTypes);
  }, [excludedTypes]);
  useEffect(() => {
    writeStorage(STORAGE.mood, activeMood);
  }, [activeMood]);

  // External URL changes (back/forward, direct link) should re-sync state.
  useEffect(() => {
    const onPop = () => {
      const next = readFiltersFromUrl();
      setSelectedTypes(next.types);
      setDateFrom(next.dateFrom);
      setDateTo(next.dateTo);
      setSelectedCity((prev) => {
        if (!next.cityId) return null;
        if (prev?.id === next.cityId) return prev;
        return { id: next.cityId, name: next.cityId, emoji: "📍" };
      });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const carousels = useMemo(
    () => (data?.success ? (data.data?.carousels ?? []) : []),
    [data],
  );
  const resolvedCity = data?.success ? (data.data?.city ?? null) : null;
  const openKikis: OpenKiki[] = data?.success
    ? (data.data?.openKikis ?? [])
    : [];

  // Client-side exclusion filter applied on top of server results.
  const filteredCarousels = useMemo(() => {
    if (excludedTypes.length === 0) return carousels;
    return carousels
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (e) => !e.types.some((t) => excludedTypes.includes(t)),
        ),
      }))
      .filter((c) => c.id === "all" || c.items.length > 0);
  }, [carousels, excludedTypes]);

  // The Tonight rail (today's events, server-pinned regardless of the browse
  // window) — pulled out so it renders right after the Kiki in BOTH the home
  // feed and the filtered category view, instead of disappearing behind a chip.
  const tonightCarousel = filteredCarousels.find(
    (c) => c.displayStyle === "list",
  );
  const tonightRail =
    tonightCarousel && tonightCarousel.items.length > 0 ? (
      <EventRail carousel={tonightCarousel} compact />
    ) : null;

  // The Kiki promo, followed by Tonight — used as the slot after the first row
  // in both views, so Kiki stays prime and Tonight sits just beneath it.
  const kikiAndTonight = (
    <>
      <KikiCarousel
        kikis={openKikis}
        cityId={effectiveCity?.id ?? null}
        isSignedIn={isSignedIn}
      />
      {tonightRail}
    </>
  );

  // The set of type-slugs that have any events in this city (from the catalog).
  const presentTypes = useMemo(() => {
    const set = new Set<string>();
    const cats = catalogData?.success
      ? (catalogData.data?.carousels ?? [])
      : [];
    for (const c of cats)
      for (const e of c.items) for (const t of e.types) set.add(t);
    return set;
  }, [catalogData]);
  // Hide featured tags with no events for this city.
  const visibleFeaturedCats = useMemo<EventCategory[]>(() => {
    const cats = FEATURED_TAG_IDS.map((id) =>
      EVENT_CATEGORIES.find((c) => c.id === id),
    ).filter((c): c is EventCategory => !!c);
    // Catalog not loaded yet → show all (avoid an empty-strip flash).
    if (presentTypes.size === 0) return cats;
    return cats.filter((cat) =>
      (cat.types ?? []).some((t) => presentTypes.has(t)),
    );
  }, [presentTypes]);

  // The featured strip allows up to two tags: one identity (gay XOR sapphic) plus
  // one other ("activity") tag. Picking a second identity replaces the first;
  // picking a second activity replaces the first — so gay+sapphic, or three tags,
  // are impossible. CategoryLanding then intersects crowd × activity.
  const handleCategory = (id: string) => {
    const cat = EVENT_CATEGORIES.find((c) => c.id === id);
    if (!cat) return;
    const isIdentity = IDENTITY_CATEGORIES.includes(id);
    let next: string[];
    if (activeCategories.includes(id)) {
      tracking.eventsFilterChanged("category", id, false);
      next = activeCategories.filter((c) => c !== id);
    } else {
      tracking.eventsFilterChanged("category", id, true);
      // Keep only the tag from the *other* slot, then add this one.
      const kept = activeCategories.filter((c) =>
        isIdentity
          ? !IDENTITY_CATEGORIES.includes(c)
          : IDENTITY_CATEGORIES.includes(c),
      );
      next = [...kept, id];
    }
    setActiveCategories(next);
    const types = next.flatMap(
      (cid) => EVENT_CATEGORIES.find((c) => c.id === cid)?.types ?? [],
    );
    setSelectedTypes([...new Set(types)]);
  };

  // Update selectedCity name/emoji from the response — avoids a separate fetch.
  useEffect(() => {
    if (!resolvedCity) return;
    setSelectedCity((prev) =>
      prev?.id === resolvedCity.id
        ? {
            id: resolvedCity.id,
            name: resolvedCity.name,
            emoji: resolvedCity.emoji,
          }
        : prev,
    );
  }, [resolvedCity]);

  return (
    <OnboardingProvider>
      <div className="min-h-screen pb-24 md:-mt-16">
        {/* Full-bleed hero with the nav overlaid on top (Partiful-style): the
            image bleeds to the very top of the page and the transparent header
            sits over it, kept legible by a top scrim. */}
        <div className="relative">
        {(() => {
          const heroName = effectiveCity ? effectiveCity.name : "everywhere";
          const heroImg = effectiveCity
            ? CITY_HERO_IMAGES[effectiveCity.id]
            : undefined;
          const heroImgClass =
            (effectiveCity && CITY_HERO_IMG_CLASS[effectiveCity.id]) ||
            HERO_IMG_DEFAULT_CLASS;
          // Scale the knockout down for longer city names so it stays one line.
          const heroSize =
            heroName.length > 13
              ? "text-3xl sm:text-6xl"
              : heroName.length > 9
                ? "text-4xl sm:text-7xl"
                : "text-5xl sm:text-8xl";
          // The city name is unresolved (still the raw id like "usa_nyc") until a
          // query fills it in — hold the knockout hidden until then, then fade it
          // in, rather than flashing the id.
          const cityResolved = !heroName.includes("_");
          return (
            <div className="relative w-full h-[260px] sm:h-[440px] flex flex-col items-center justify-center overflow-hidden bg-neutral-900 mb-6">
              {heroImg ? (
                <img
                  key={heroImg}
                  src={heroImg}
                  alt=""
                  aria-hidden
                  className={`absolute inset-0 w-full h-full object-cover animate-fade-in ${heroImgClass}`}
                />
              ) : (
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-br from-primary-500 via-secondary-500 to-grape-700 [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0.95)_72%,transparent)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0.95)_72%,transparent)]"
                />
              )}
              {heroImg && effectiveCity && CITY_HERO_GRAIN[effectiveCity.id] && (
                <div
                  aria-hidden
                  className={`absolute inset-0 z-0 pointer-events-none mix-blend-overlay ${CITY_HERO_GRAIN[effectiveCity.id].opacity}`}
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='laGrain'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.5' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23laGrain)'/%3E%3C/svg%3E\")",
                    backgroundSize: CITY_HERO_GRAIN[effectiveCity.id].size,
                  }}
                />
              )}
              <span className="relative z-10 mb-1 text-sm font-semibold uppercase tracking-[0.22em] text-white [text-shadow:0_1px_12px_rgba(0,0,0,0.65)]">
                explore
              </span>
              {effectiveCity && CITY_HERO_FONT[effectiveCity.id] ? (
                // Pre-rendered wordmark: the city name outlined (in its bespoke
                // font) into a tiny static SVG, so the page never downloads a
                // webfont just to draw the hero title. Regenerate after adding a
                // city: /tmp gen_wordmarks (FontShare woff2 → public/img/hero/wordmarks).
                <img
                  src={`/img/hero/wordmarks/${effectiveCity.id}.svg`}
                  alt={heroName}
                  className={`relative z-10 h-16 w-auto max-w-[86vw] px-6 object-contain transition-opacity duration-500 [filter:drop-shadow(0_2px_16px_rgba(0,0,0,0.4))] sm:h-28 ${cityResolved ? "opacity-100" : "opacity-0"}`}
                />
              ) : (
                <span
                  className={`relative z-10 px-6 text-white tracking-tight whitespace-nowrap transition-opacity duration-500 [text-shadow:0_2px_16px_rgba(0,0,0,0.4)] ${cityResolved ? "opacity-100" : "opacity-0"} ${heroSize}`}
                  style={{ fontFamily: "'Sentient', serif" }}
                >
                  {heroName}
                </span>
              )}
            </div>
          );
        })()}
        {/* Signed-out: the events header IS the top bar, so make it the frosted-
            glass fixed bar (hero/events blur through it on scroll). Signed-in, the
            MainApp <Navigation/> is the top bar (also glass-on-scroll), so we don't
            render a second one here — the hero just bleeds under that nav. */}
        {!isSignedIn && (
          <div className="fixed inset-x-0 top-0 z-40 bg-white/30 dark:bg-black/30 backdrop-blur-[16px] backdrop-saturate-[1.8] backdrop-brightness-110">
            <EventsHeader signedIn={false} />
          </div>
        )}
        </div>

        {/* Bottom vignette: a fixed transparent→shadow gradient so content gains
            depth as it scrolls off the bottom edge (Partiful-style). */}
        <div
          aria-hidden
          className="pointer-events-none fixed inset-x-0 bottom-0 z-30 h-24 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
        />

        {/* Beta notice — signed-in only, dismissible (persisted to localStorage) */}
        {isSignedIn && !betaBannerDismissed && (
          <BetaBanner
            onFeedback={() => setFeedbackOpen(true)}
            onDismiss={dismissBetaBanner}
          />
        )}

        {/* Outclose featured rail leads, directly under the hero and above the
            filters. Sourced from the type-unfiltered catalog so the monetized
            product stays visible even when a tag filter is active (it ignores the
            timeframe/tags anyway). */}
        {(() => {
          const cats = catalogData?.success
            ? (catalogData.data?.carousels ?? [])
            : [];
          const outclose = cats.find((c) => c.id === "outclose");
          const outcloseItems = outclose?.items ?? [];
          return outcloseItems.length > 0 ? (
            <OutcloseDinnerRail items={outcloseItems} guestNoun={guestNoun} />
          ) : null;
        })()}

        {/* Posh-style inline filter composer: a readable sentence — "[timeframe]
            in [city]" — whose underlined words open small inline dropdowns. */}
        <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto pt-1 mb-1 px-4 sm:px-6">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xl sm:text-2xl text-base-content">
            <InlineDropdown active={!!(dateFrom || dateTo)} label={timeframeLabel}>
              {(close) => (
                <>
                  {TIMEFRAMES.map((tf) => (
                    <DropdownOption
                      key={tf.key}
                      label={tf.label}
                      selected={tf.key === currentTimeframeKey}
                      onClick={() => {
                        const r = timeframeRange(tf.key);
                        tracking.eventsFilterChanged(
                          "date",
                          tf.label,
                          !!(r.from || r.to),
                        );
                        setDateFrom(r.from);
                        setDateTo(r.to);
                        close();
                      }}
                    />
                  ))}
                  <div className="my-1 border-t border-base-300" />
                  <DropdownOption
                    label="Choose dates…"
                    onClick={() => {
                      close();
                      setDateModalOpen(true);
                    }}
                  />
                </>
              )}
            </InlineDropdown>
            <span className="font-Poppins font-normal text-base-content/50">
              in
            </span>
            <InlineDropdown
              active={!!effectiveCity}
              label={effectiveCity ? effectiveCity.name : "your city"}
              align="right"
            >
              {(close) => (
                <CityMenu
                  cities={cityOptions}
                  selectedId={effectiveCity?.id}
                  nearest={closestCity}
                  onSelect={(c) => {
                    tracking.eventsFilterChanged("city", c.name ?? c.id, true);
                    setSelectedCity({ id: c.id, name: c.name, emoji: c.emoji ?? "" });
                    close();
                  }}
                />
              )}
            </InlineDropdown>
          </div>
        </div>

        {/* Category quick-filter strip */}
        <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto mt-3">
          <div
            ref={categoryStripRef}
            {...categoryStripDrag}
            className="flex gap-2 overflow-x-auto px-4 sm:px-6 cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {/* Date presets (today / this weekend) live in the timeframe dropdown.
                Expose only a curated few tags; the rest open via "More" (the full
                tag picker) so the strip stays clean. */}
            {/* "All" — active by default (no tag filter), clears any selection. */}
            <button
              type="button"
              onClick={() => {
                tracking.eventsFilterChanged("category", "all", true);
                setActiveCategories([]);
                setSelectedTypes([]);
              }}
              className={`
                inline-flex items-center gap-2 px-4 h-9 sm:h-10 rounded-full text-base font-semibold
                whitespace-nowrap shrink-0 border backdrop-blur-2xl transition-colors
                ${
                  selectedTypes.length === 0
                    ? "bg-black/10 dark:bg-white/[0.15] border-black/40 dark:border-white/60 text-base-content"
                    : "bg-black/[0.05] dark:bg-white/[0.05] border-transparent text-base-content/90 hover:border-black/20 dark:hover:border-white/25"
                }
              `}
            >
              <span className="text-xl leading-none">🔍</span>
              <span>All</span>
            </button>
            {visibleFeaturedCats.map((cat) => {
              const active = activeCategories.includes(cat.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCategory(cat.id)}
                    className={`
                  inline-flex items-center gap-2 px-4 h-9 sm:h-10 rounded-full text-base font-semibold
                  whitespace-nowrap shrink-0 border backdrop-blur-2xl transition-colors
                  ${
                    active
                      ? "bg-black/10 dark:bg-white/[0.15] border-black/40 dark:border-white/60 text-base-content"
                      : "bg-black/[0.05] dark:bg-white/[0.05] border-transparent text-base-content/90 hover:border-black/20 dark:hover:border-white/25"
                  }
                `}
                  >
                    <span className="text-xl leading-none">{cat.emoji}</span>
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            {/* More → full tag picker (reuses the existing EventTypeSelectModal) */}
            <button
              type="button"
              onClick={() => setTypeModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 h-9 sm:h-10 rounded-full text-base font-semibold whitespace-nowrap shrink-0 border border-transparent bg-black/[0.05] dark:bg-white/[0.05] text-base-content/90 backdrop-blur-2xl transition-colors hover:border-black/20 dark:hover:border-white/25"
            >
              <Filter className="w-[18px] h-[18px]" />
              <span>More</span>
            </button>
          </div>
        </div>

        {/* Exclusion filter row — hide tags you don't want to see
      {availableTags.length > 0 && (
        <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mt-2 mb-1">
          <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <span className="text-xs text-neutral-400 dark:text-neutral-500 shrink-0 select-none">Hide:</span>
            {availableTags.map((tag) => {
              const excluded = excludedTypes.includes(tag.slug);
              return (
                <button
                  key={tag.slug}
                  type="button"
                  onClick={() =>
                    setExcludedTypes((prev) =>
                      excluded ? prev.filter((s) => s !== tag.slug) : [...prev, tag.slug],
                    )
                  }
                  className={`
                    inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                    whitespace-nowrap shrink-0 transition-colors border
                    ${excluded
                      ? "bg-neutral-700 border-neutral-600 text-neutral-300 line-through opacity-60"
                      : "bg-base-200 border-base-300 hover:bg-base-300 text-base-content"
                    }
                  `}
                >
                  <span>{tag.emoji}</span>
                  <span>{tag.label}</span>
                  {excluded && <X className="w-2.5 h-2.5 ml-0.5 no-underline" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Mood preset chips */}
        {/* {availablePresets.length > 0 && (
        <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mt-2 mb-1">
          <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {MOOD_PRESETS.filter(p => availablePresets.includes(p.id)).map((preset) => {
              const active = activeMood === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setActiveMood(active ? null : preset.id)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                    whitespace-nowrap shrink-0 transition-colors border
                    ${active
                      ? "bg-primary-500 text-white border-primary-500"
                      : "bg-base-200 border-base-300 hover:bg-base-300 text-base-content"
                    }
                  `}
                >
                  <span>{preset.emoji}</span>
                  <span>{preset.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )} */}

        <EventTypeSelectModal
          isOpen={typeModalOpen}
          onClose={() => setTypeModalOpen(false)}
          selected={selectedTypes}
          onChange={(types) => {
            tracking.eventsFilterChanged(
              "type",
              types.join(",") || null,
              types.length > 0,
            );
            setSelectedTypes(types);
            setActiveCategories([]);
          }}
          title={t("filters.filterByTags")}
          availableTypes={presentTypes}
          excludeGroups={["food"]}
        />

        <DateFilterModal
          isOpen={dateModalOpen}
          onClose={() => setDateModalOpen(false)}
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => {
            tracking.eventsFilterChanged(
              "date",
              formatDateRange(f, t) || null,
              !!(f || t),
            );
            setDateFrom(f);
            setDateTo(t);
          }}
        />

        <CitySelectModal
          isOpen={cityModalOpen}
          onClose={() => setCityModalOpen(false)}
          selectedId={selectedCity?.id}
          onSelect={(c) => {
            tracking.eventsFilterChanged("city", c.name ?? c.id, true);
            setSelectedCity(c);
          }}
          hideComingSoon
        />

        <main className="relative w-full sm:w-[80vw] max-w-[1504px] mx-auto mt-6">
          {selectedTypes.length > 0 ? (
          <CategoryLanding
            types={selectedTypes}
            cityId={effectiveCity?.id ?? null}
            cityName={effectiveCity?.name ?? null}
            dateFrom={dateFrom}
            dateTo={dateTo}
            excludedTypes={excludedTypes}
            kikiSlot={kikiAndTonight}
          />
          ) : (
          <>
          {isLoading && (
            <div className="flex items-center justify-center py-20">
              <Loader />
            </div>
          )}

          {error && (
            <div className="text-center py-20 text-neutral-500 dark:text-neutral-400 px-4">
              {t("states.loadError")}
            </div>
          )}

          {!isLoading && !error && filteredCarousels.length === 0 && (
            <div className="text-center py-20 text-neutral-500 dark:text-neutral-400 px-4">
              {carousels.length > 0
                ? t("states.allHidden")
                : t("states.empty")}
            </div>
          )}

          <div className="space-y-8 sm:space-y-10 animate-fade-in">
            {/* One row of (non-Outclose) discovery events, then the Kiki — a prime,
                monetized feature, so it sits high, but after a taste of real
                events rather than leading the whole feed. */}
            {(() => {
              const rails = filteredCarousels.filter(
                (c) => c.id !== "outclose" && c.displayStyle !== "list",
              );
              const kiki = kikiAndTonight;
              if (rails.length === 0) return kiki;
              return rails.map((carousel, i) => (
                <Fragment key={carousel.id}>
                  <EventRail carousel={carousel} compact />
                  {i === 0 && kiki}
                </Fragment>
              ));
            })()}
          </div>

          {!isLoading && !error && (
            <div className="mt-8 sm:mt-10">
              <AllEventsSection
                filters={{
                  types: selectedTypes,
                  city: effectiveCity?.id ?? null,
                  dateFrom,
                  dateTo,
                }}
                excludedTypes={excludedTypes}
              />
            </div>
          )}
          </>
          )}

          {customEventsEnabled && !isSignedIn && !isLoading && !error && (
            <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mt-16 mb-8 flex flex-col items-center text-center gap-3">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {t("addEvent.prompt")}
              </p>
              <Link
                href="/events/new"
                onClick={() => tracking.eventsAddEventClicked()}
                className="btn btn-primary inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                <span>{t("addEvent.cta")}</span>
              </Link>
            </div>
          )}
        </main>

        {/* Signed-in users get the compact legal disclaimer; signed-out get the
          full marketing footer. */}
        {isSignedIn ? (
          <>
            <LandingFooter size="small" />

            <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mt-16 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setFeedbackOpen(true)}
                className="btn btn-primary"
              >
                <MessageSquare className="w-4 h-4 mr-2 inline-block" />
                {t("actions.giveFeedback")}
              </button>
              {customEventsEnabled && (
                <Link
                  href="/events/new"
                  onClick={() => tracking.eventsAddEventClicked()}
                  className="btn btn-primary inline-flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t("addEvent.cta")}</span>
                </Link>
              )}
            </div>
          </>
        ) : (
          <div className="mt-16">
            <LandingFooter size="large" appStoreSource="events_footer" />
          </div>
        )}
      </div>

      <FeedbackDialog
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
    </OnboardingProvider>
  );
}

// ---------------------------------------------------------------------------

// Inline filter-composer dropdown — a bold, underlined word with a chevron that
// reads as part of a sentence (Posh-style) and opens a small anchored popover.
// `children` is a render-prop receiving a `close` callback for option clicks.
function InlineDropdown({
  label,
  active,
  align = "left",
  children,
}: {
  label: string;
  active?: boolean;
  align?: "left" | "right";
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="group inline-flex items-center gap-1 font-Poppins font-semibold transition-colors hover:text-primary-500"
      >
        <span
          className={`underline decoration-2 underline-offset-[6px] ${
            active
              ? "decoration-primary-500"
              : "decoration-base-content/25 group-hover:decoration-primary-500/60"
          }`}
        >
          {label}
        </span>
        <ChevronDown
          className={`h-4 w-4 opacity-50 transition-transform group-hover:opacity-100 sm:h-5 sm:w-5 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className={`absolute z-40 mt-2 min-w-[210px] rounded-2xl border border-base-300 bg-base-100 p-1.5 shadow-2xl ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

function DropdownOption({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-base font-Poppins font-medium transition-colors ${
        selected
          ? "bg-primary-500/10 text-primary-500"
          : "text-base-content hover:bg-base-200"
      }`}
    >
      <span className="truncate">{label}</span>
      {selected && <span className="text-primary-500">✓</span>}
    </button>
  );
}

// The city dropdown body: a searchable, scrollable list with a "Near me"
// shortcut and an inline search field (Posh-style) — never opens a modal.
function CityMenu({
  cities,
  selectedId,
  nearest,
  onSelect,
}: {
  cities: { id: string; name: string; emoji?: string }[];
  selectedId?: string;
  nearest?: { id: string; name: string; emoji?: string };
  onSelect: (c: { id: string; name: string; emoji?: string }) => void;
}) {
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const query = q.trim().toLowerCase();
  const filtered = query
    ? cities.filter((c) => c.name.toLowerCase().includes(query))
    : cities;
  return (
    <div className="w-[232px]">
      <div className="max-h-64 overflow-y-auto">
        {!query && nearest && (
          <DropdownOption label="📍 Near me" onClick={() => onSelect(nearest)} />
        )}
        {filtered.map((c) => (
          <DropdownOption
            key={c.id}
            label={`${c.emoji ?? "📍"} ${c.name}`}
            selected={c.id === selectedId}
            onClick={() => onSelect(c)}
          />
        ))}
        {filtered.length === 0 && (
          <p className="px-3 py-2 text-sm text-base-content/50">
            No cities found
          </p>
        )}
      </div>
      <div className="mt-1 border-t border-base-300 pt-1.5">
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Enter a city"
          className="w-full rounded-xl bg-base-200 px-3 py-2 text-base font-Poppins font-normal text-base-content outline-none placeholder:text-base-content/40 focus:ring-2 focus:ring-primary-500/40"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

// The events-index header. Signed-out visitors get the full marketing bar;
// signed-in users get the compact (logo-only) variant. Rendered inside
// <OnboardingProvider> so "How it works" can open the same onboarding intro,
// and the login button drops into the in-app sign-in flow.
function EventsHeader({ signedIn }: { signedIn: boolean }) {
  const { t } = useT("landing");
  const { openSignIn } = useAuth();
  const onboarding = useOnboardingGate();

  return (
    <div className="">
      <SiteHeader
        size={signedIn ? "small" : "large"}
        links={
          <>
            <button
              onClick={() => onboarding?.openOnboarding()}
              className="text-type-950 dark:text-neutral-100 hover:text-primary-500 transition-colors font-medium"
            >
              {t("nav.howItWorks")}
            </button>
          </>
        }
        login={
          <button
            onClick={() => openSignIn()}
            className="btn-primary px-4 py-2 w-auto"
          >
            {t("app:common.buttons.login")}
          </button>
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

// Beta notice shown to signed-in users on the new Events page. Rendered inside
// <OnboardingProvider> so the "How it works" button can open the onboarding
// intro, same as the header.
function BetaBanner({
  onFeedback,
  onDismiss,
}: {
  onFeedback: () => void;
  onDismiss: () => void;
}) {
  const { t } = useT("events");
  const onboarding = useOnboardingGate();

  // Fire once when the banner is rendered to a user.
  useEffect(() => {
    tracking.eventsBetaBannerShown();
  }, []);

  return (
    <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mb-3">
      <div className="relative flex flex-col items-center gap-3 overflow-hidden rounded-2xl border border-primary-500/30 bg-gradient-to-br from-primary-500/15 via-secondary-500/10 to-accent-500/15 px-4 py-5 text-center">

        <div className="space-y-3">
          <p className="text-sm text-base-content/80 max-w-xl mx-auto">
            {t("banner.line1")}
            <br />
            {t("banner.line2")}
          </p>
          <p className="text-sm text-base-content/80 max-w-xl mx-auto">
            {t("banner.line3")}
          </p>
        </div>
        <div className="mt-1 flex w-full max-w-sm items-center gap-2">
          <button
            type="button"
            onClick={() => {
              tracking.eventsBetaBannerClicked("feedback");
              onFeedback();
            }}
            className="btn btn-primary flex-1 whitespace-nowrap"
          >
            <MessageSquare className="w-4 h-4 mr-1.5 inline-block" />
            {t("actions.giveFeedback")}
          </button>
          <button
            type="button"
            onClick={() => {
              tracking.eventsBetaBannerClicked("how_it_works");
              onboarding?.openOnboarding();
            }}
            className="btn btn-primary flex-1 whitespace-nowrap"
          >
            {t("actions.howItWorks")}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            tracking.eventsBetaBannerClicked("dismiss");
            onDismiss();
          }}
          aria-label="Dismiss"
          className="absolute top-2.5 right-2.5 p-1 rounded-full text-base-content/50 hover:text-base-content hover:bg-base-300 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

// Outclose's curated dinners get a distinct horizontal "detail card" (vs. the
// square flyer rails) — the monetized product reads differently from discovery.
// Personalize the dinner card's guest noun: "gays" by default, "sapphics" for
// sapphic-community users (explicit community pick wins; else gender identity).
function guestNounFor(
  quiz:
    | { community?: string | null; genderIdentity?: string[] | null }
    | null
    | undefined,
): string {
  if (quiz?.community === "sapphic") return "sapphics";
  if (quiz?.community === "men") return "gays";
  return isSapphicUser(quiz ?? {}) ? "sapphics" : "gays";
}

function OutcloseDinnerCard({
  event,
  guestNoun,
}: {
  event: EventCardType;
  guestNoun: string;
}) {
  const when = event.date
    ? formatRelativeEventDate(event.date, event.time)
    : null;
  // Re-label the curated Outclose meals for the viewer: "Gay/Sapphic dinner"
  // (or brunch/drinks/coffee), from the meal in the event name + the viewer's
  // identity.
  const identity = guestNoun === "sapphics" ? "Sapphic" : "Gay";
  const name = event.name ?? "";
  const meal = /brunch/i.test(name)
    ? "brunch"
    : /coffee/i.test(name)
      ? "coffee"
      : /drinks/i.test(name)
        ? "drinks"
        : "dinner";
  const title = `${identity} ${meal}`;
  return (
    <Link
      href={event.link}
      onClick={() =>
        tracking.eventsCardClicked(event.slug ?? event.link, {
          isOutclose: true,
        })
      }
      className="group flex h-full items-stretch gap-3 sm:gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:p-4 transition-colors hover:bg-white/[0.07]"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        {when && (
          <p className="text-[13px] font-bold uppercase tracking-wider text-white">
            {when}
          </p>
        )}
        <h3 className="mt-1 font-Poppins text-lg sm:text-xl font-bold leading-tight line-clamp-1">
          {title}
        </h3>
        <p className="mt-1.5 sm:mt-2 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-[15px] text-base-content/70">
          <Users className="h-4 w-4 sm:h-[18px] sm:w-[18px] shrink-0 opacity-70" />
          <span className="min-w-0 truncate">4–6 matched {guestNoun}</span>
        </p>
        <p className="mt-0.5 sm:mt-1 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-[15px] text-base-content/55">
          <MapPin className="h-4 w-4 sm:h-[18px] sm:w-[18px] shrink-0 opacity-70" />
          <span className="min-w-0 truncate">
            {event.venue.city ?? "New York City"}
          </span>
        </p>
        <div className="mt-auto pt-3 sm:pt-4">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-white/20 bg-white/10 px-3.5 sm:px-4 py-2 sm:py-2.5 text-sm font-semibold text-white backdrop-blur-md transition group-hover:scale-[1.03] group-hover:bg-white/[0.16]">
            Book a seat <span aria-hidden>→</span>
          </span>
        </div>
      </div>
      <div className="relative aspect-square w-24 shrink-0 self-center overflow-hidden rounded-xl bg-neutral-900 sm:w-36 xl:w-28">
        {event.coverImageUrl && (
          <img
            src={event.coverImageUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        )}
      </div>
    </Link>
  );
}

// "See more" dates live in the rail itself — a horizontal slider that matches
// the discovery rails (page-scroll arrows + drag), showing ~2 dinners at a time.
function OutcloseDinnerRail({
  items,
  guestNoun,
}: {
  items: EventCardType[];
  guestNoun: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanLeft(el.scrollLeft > 4);
      setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [items.length]);

  const scrollByPage = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir * Math.round(el.clientWidth * 0.9),
      behavior: "smooth",
    });
  };

  const onMouseDown = (e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    hasDragged.current = false;
    dragStartX.current = e.pageX - el.getBoundingClientRect().left;
    dragScrollLeft.current = el.scrollLeft;
    setIsDragging(true);
    const onMove = (ev: MouseEvent) => {
      const x = ev.pageX - el.getBoundingClientRect().left;
      const delta = x - dragStartX.current;
      if (Math.abs(delta) > 3) hasDragged.current = true;
      el.scrollLeft = dragScrollLeft.current - delta;
    };
    const onUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return (
    <section className="w-full sm:w-[80vw] max-w-[1504px] mx-auto mb-7 mt-1 animate-fade-in">
      <div className="px-4 sm:px-6 flex items-end justify-between gap-3 mb-4">
        <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">
          A table of strangers
        </h2>
        <div className="hidden sm:flex items-center gap-1">
          <button
            type="button"
            onClick={() => scrollByPage(-1)}
            disabled={!canLeft}
            aria-label="Scroll dinners left"
            className="p-1.5 rounded-full bg-base-200 hover:bg-base-300 disabled:opacity-40 disabled:cursor-default transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollByPage(1)}
            disabled={!canRight}
            aria-label="Scroll dinners right"
            className="p-1.5 rounded-full bg-base-200 hover:bg-base-300 disabled:opacity-40 disabled:cursor-default transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto px-4 sm:px-6 pb-2 -mb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
        onMouseDown={onMouseDown}
        onClickCapture={onClickCapture}
      >
        {items.map((e) => (
          <div key={e.link} className="shrink-0 w-[88%] sm:w-[47%] xl:w-[31.5%]">
            <OutcloseDinnerCard event={e} guestNoun={guestNoun} />
          </div>
        ))}
      </div>
    </section>
  );
}

function EventRail({
  carousel,
  compact,
}: {
  carousel: EventCarousel;
  compact?: boolean;
}) {
  if (carousel.displayStyle === "hero")
    return <HeroSection event={carousel.items[0]} />;
  if (carousel.displayStyle === "list")
    return <ListSection carousel={carousel} />;
  if (carousel.layout === "grid")
    return <GridRail carousel={carousel} compact={compact} />;
  return <CarouselRail carousel={carousel} compact={compact} />;
}

function HeroSection({ event }: { event: EventCardType | undefined }) {
  const { t } = useT("events");
  if (!event) return null;
  const parsed = parseUTCDate(event.date);
  const displayTags = event.types
    .map((slug) => getEventType(slug))
    .filter((t): t is NonNullable<typeof t> => t !== null)
    .filter((t, _, arr) => !(t.slug === "queer" && arr.length > 2))
    .slice(0, 3);
  const hostLine = event.host?.group?.name ?? event.host?.firstName ?? null;

  return (
    <section className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6">
      <Link
        href={event.link}
        onClick={() =>
          tracking.eventsCardClicked(event.slug ?? event.link, {
            isOutclose: event.isOutclose,
            carouselType: "hero",
          })
        }
        className="block group relative rounded-2xl overflow-hidden bg-neutral-900 aspect-[4/3] sm:aspect-[16/7]"
        style={{ minHeight: "100px" }}
      >
        {event.coverImageUrl ? (
          <img
            src={event.coverImageUrl}
            alt={event.name ?? ""}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <EventPoster
            name={event.name ?? t("card.untitled")}
            label={event.posterLabel}
            types={event.types}
            className="absolute inset-0 w-full h-full"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />

        {/* Top-left: Featured badge + type tags */}
        <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold text-white uppercase tracking-widest"
            style={{
              background:
                "linear-gradient(135deg, rgba(139,92,246,0.88) 0%, rgba(236,72,153,0.88) 100%)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            ✦ {t("card.featured")}
          </span>
          {displayTags.map((tag) => (
            <span
              key={tag.slug}
              className="inline-flex items-center gap-0.5 rounded-full text-white font-semibold"
              style={{
                backgroundColor: "rgba(12,8,28,0.65)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.12)",
                fontSize: "0.68rem",
                padding: "3px 8px",
              }}
            >
              <span>{tag.emoji}</span>
              <span className="ml-0.5">{tag.label}</span>
            </span>
          ))}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
          <p className="text-xs text-white/55 uppercase tracking-widest mb-1.5">
            {parsed.weekday}, {parsed.monthShort} {parsed.day}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight group-hover:text-primary-300 transition-colors line-clamp-2">
            {event.name}
          </h2>
          <div className="mt-2 flex items-end justify-between gap-4">
            <div className="min-w-0">
              {(event.venue.name || event.venue.city) && (
                <p className="text-sm text-white/60 truncate">
                  {[
                    event.venue.name,
                    event.venue.neighborhood,
                    event.venue.city,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
              <p className="text-xs text-white/45 mt-0.5">
                {[
                  hostLine && t("card.by", { host: hostLine }),
                  event.attendeeCount > 0 &&
                    t("card.going", { count: event.attendeeCount }),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <span className="shrink-0 inline-flex items-center gap-1 px-4 py-2 rounded-full bg-white/95 text-neutral-900 text-sm font-semibold group-hover:bg-white transition-colors">
              {t("card.viewEvent")}{" "}
              <span aria-hidden className="ml-0.5">
                →
              </span>
            </span>
          </div>
        </div>
      </Link>
    </section>
  );
}

// Is this event happening right now? Times are naive wall-clock (no timezone
// conversion, matching the rest of the app), interpreted in the viewer's local
// zone — a close-enough signal for the "tonight" agenda.
function isHappeningNow(
  date: string,
  time: string,
  endTime: string | null,
): boolean {
  if (!time || time === "00:00:00") return false;
  const start = new Date(`${date}T${time}`);
  if (Number.isNaN(start.getTime())) return false;
  let end = endTime
    ? new Date(`${date}T${endTime}`)
    : new Date(start.getTime() + 3 * 3_600_000);
  if (end.getTime() <= start.getTime())
    end = new Date(end.getTime() + 86_400_000); // crosses midnight
  const now = Date.now();
  return now >= start.getTime() && now <= end.getTime();
}

// "Today" / "Tomorrow" / weekday label for the timeline header node, plus the
// full weekday so the header can read "Today · Thursday" like the inspiration.
function relativeDayParts(dateStr: string): { label: string; weekday: string } {
  const d = new Date(dateStr);
  const now = new Date();
  const eventUTC = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const todayUTC = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  const days = Math.round((eventUTC - todayUTC) / 86_400_000);
  const weekday = parseUTCDate(dateStr).weekday;
  const label = days === 0 ? "Today" : days === 1 ? "Tomorrow" : weekday;
  return { label, weekday };
}

// Small round host avatar — the group photo when there is one, else a monogram.
function HostAvatar({ host }: { host: EventCardType["host"] }) {
  const img = host?.group?.imageUrl ?? null;
  const name = host?.group?.name ?? host?.firstName ?? "";
  const initial = name.trim().charAt(0).toUpperCase() || "·";
  return (
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-base-300 ring-2 ring-base-100">
      {img ? (
        <img src={img} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-[10px] font-semibold text-base-content/60">
          {initial}
        </span>
      )}
    </span>
  );
}

function ListSection({ carousel }: { carousel: EventCarousel }) {
  const { t } = useT("events");
  const INITIAL = 5;
  const STEP = 10;
  const [count, setCount] = useState(INITIAL);
  const sectionRef = useRef<HTMLElement>(null);
  if (carousel.items.length === 0) return null;
  const visible = carousel.items.slice(0, count);
  const remaining = carousel.items.length - count;

  // The agenda shares one day when every event falls on the same date (the
  // "Tonight" rail) — only then do we anchor it with a day header node.
  const firstDate = carousel.items[0]?.date ?? null;
  const sameDay =
    firstDate != null && carousel.items.every((e) => e.date === firstDate);
  const header = sameDay ? relativeDayParts(firstDate) : null;

  return (
    <section ref={sectionRef}>
      {carousel.title && (
        <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mb-4">
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">{carousel.title}</h2>
        </div>
      )}
      {/* Chronological agenda — a dashed timeline with a node per event, à la
          Luma. Each event is its own card container; the dashed line runs down
          the gutter, anchored by a day header node at the top. */}
      <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6">
        <div className="relative">
          {header && (
            <div className="relative mb-3 flex items-baseline gap-2">
              <span
                aria-hidden
                className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-base-content/40 ring-4 ring-base-100"
              />
              <span className="text-sm font-bold text-base-content">
                {header.label}
              </span>
              {header.label !== header.weekday && (
                <span className="text-sm text-base-content/50">
                  {header.weekday}
                </span>
              )}
            </div>
          )}
          <div className="space-y-3.5">
            {visible.map((event, i) => {
              const tags = event.types
                .slice(0, 2)
                .map((s) => getEventType(s))
                .filter((x): x is NonNullable<typeof x> => x !== null);
              const time =
                event.time && event.time !== "00:00:00"
                  ? formatTimeToDisplay(event.time)
                  : null;
              const live = isHappeningNow(
                event.date,
                event.time,
                event.endTime,
              );
              const host =
                event.host?.group?.name ??
                (event.host
                  ? `${event.host.firstName}${event.host.lastName ? " " + event.host.lastName : ""}`
                  : null);
              const place =
                [event.venue.name, event.venue.neighborhood ?? event.venue.city]
                  .filter(Boolean)
                  .join(" · ") ||
                event.venue.city ||
                null;
              return (
                <Link
                  key={event.link}
                  href={event.link}
                  onClick={() =>
                    tracking.eventsCardClicked(event.slug ?? event.link, {
                      isOutclose: event.isOutclose,
                      carouselType: "list",
                      position: i,
                    })
                  }
                  className="group relative flex items-stretch gap-4 rounded-2xl border border-content bg-base-100 p-3.5 shadow-sm transition-all hover:border-base-content/20 hover:shadow-md"
                >
                  {/* timeline node, sitting on the dashed line in the gutter */}
                  <span
                    aria-hidden
                    className={`absolute -left-[21px] top-[1.4rem] h-2.5 w-2.5 rounded-full ring-4 ring-base-100 ${
                      live ? "bg-primary-500" : "bg-base-content/25"
                    }`}
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    {/* LIVE badge + start time */}
                    <div className="flex items-center gap-2">
                      {live && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-primary-600">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-75" />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary-500" />
                          </span>
                          Live
                        </span>
                      )}
                      {time && (
                        <p className="text-[13px] font-semibold text-base-content/50">
                          {time}
                        </p>
                      )}
                    </div>
                    <h3 className="mt-0.5 line-clamp-2 text-base font-semibold leading-snug transition-colors group-hover:text-primary-500 sm:text-lg">
                      {event.name}
                    </h3>
                    {host && (
                      <p className="mt-1.5 flex min-w-0 items-center gap-1.5 text-sm text-base-content/60">
                        <HostAvatar host={event.host} />
                        <span className="truncate">By {host}</span>
                      </p>
                    )}
                    {place && (
                      <p className="mt-1 flex items-center gap-1.5 text-sm text-base-content/55">
                        <MapPin className="h-4 w-4 shrink-0 opacity-70" />
                        <span className="truncate">{place}</span>
                      </p>
                    )}
                    {(tags.length > 0 || event.attendeeCount > 0) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {tags.map((tag) => (
                          <span
                            key={tag.slug}
                            className="inline-flex items-center gap-1 rounded-full bg-base-200 px-2.5 py-1 text-xs font-medium text-base-content/80"
                          >
                            <span className="leading-none">{tag.emoji}</span>
                            {tag.label}
                          </span>
                        ))}
                        {event.attendeeCount > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-base-200 px-2.5 py-1 text-xs font-medium text-base-content/70">
                            <Users className="h-3.5 w-3.5 opacity-70" />
                            {t("card.going", { count: event.attendeeCount })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="relative aspect-square w-24 shrink-0 self-center overflow-hidden rounded-xl bg-neutral-900 sm:w-28">
                    {event.coverImageUrl ? (
                      <img
                        src={event.coverImageUrl}
                        alt={event.name ?? t("card.untitled")}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <EventPoster
                        name={event.name ?? t("card.untitled")}
                        label={event.posterLabel}
                        types={event.types}
                        className="absolute inset-0 h-full w-full"
                      />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      {(remaining > 0 || count > INITIAL) && (
        <div className="mt-4 flex justify-center gap-2">
          {remaining > 0 && (
            <button
              type="button"
              onClick={() => {
                tracking.eventsCarouselExpanded("list");
                setCount((c) => Math.min(carousel.items.length, c + STEP));
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-base-200 hover:bg-base-300 text-sm font-medium transition-colors"
            >
              <span>
                {t("card.showMore", { count: Math.min(STEP, remaining) })}
              </span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          )}
          {count > INITIAL && (
            <button
              type="button"
              onClick={() => {
                setCount(INITIAL);
                sectionRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-base-200 hover:bg-base-300 text-sm font-medium transition-colors"
            >
              <span>Show less</span>
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function GridRail({
  carousel,
  compact,
}: {
  carousel: EventCarousel;
  compact?: boolean;
}) {
  const { t } = useT("events");
  const [expanded, setExpanded] = useState(false);
  const maxVisible = 8;
  const visible = expanded
    ? carousel.items
    : carousel.items.slice(0, maxVisible);
  const hidden = carousel.items.length - maxVisible;

  return (
    <section>
      <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mb-4">
        <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">{carousel.title}</h2>
      </div>
      <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6">
        <div
          className={
            compact
              ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4"
              : "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
          }
        >
          {visible.map((event) => (
            <EventCardComponent
              key={event.link}
              event={event}
              largeCards={false}
            />
          ))}
        </div>
        {!expanded && hidden > 0 && (
          <div className="flex justify-center mt-4">
            <button
              type="button"
              onClick={() => {
                tracking.eventsCarouselExpanded("grid");
                setExpanded(true);
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-base-200 hover:bg-base-300 text-sm font-medium transition-colors"
            >
              <span>{t("card.showMore", { count: hidden })}</span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function AllEventsSection({
  filters,
  excludedTypes,
}: {
  filters: {
    types: string[];
    city: string | null;
    dateFrom: string | null;
    dateTo: string | null;
  };
  excludedTypes: string[];
}) {
  const { t } = useT("events");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useAllEvents(filters);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const allItems = useMemo(() => {
    const pages = data?.pages ?? [];
    return pages
      .flatMap((p) => (p.success && p.data ? p.data.items : []))
      .filter((e) => !e.types.some((t) => excludedTypes.includes(t)));
  }, [data, excludedTypes]);

  const total = data?.pages[0]?.success ? (data.pages[0].data?.total ?? 0) : 0;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <section>
        <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mb-4">
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">
            {t("sections.allEvents")}
          </h2>
        </div>
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      </section>
    );
  }

  if (allItems.length === 0) return null;

  return (
    <section>
      <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mb-4 flex items-baseline gap-3">
        <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">All Events</h2>
        {total > 0 && (
          <span className="text-sm text-neutral-400 dark:text-neutral-500">
            {total}
          </span>
        )}
      </div>
      <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4">
          {allItems.map((event) => (
            <EventCardComponent
              key={event.link}
              event={event}
              largeCards={false}
            />
          ))}
        </div>
        <div ref={sentinelRef} className="flex justify-center py-6">
          {isFetchingNextPage && <Loader size={20} />}
        </div>
      </div>
    </section>
  );
}
